'use client';
import Script from 'next/script';
import { useEffect, useRef } from 'react';

import {
  GTM_ACTION_CLICK,
  GTM_EVENT_CHAT_DOWNLOAD_CLICK,
  GTM_EVENT_CHAT_FEEDBACK_CLICK,
  GTM_EVENT_CHAT_FEEDBACK_INPUT,
  GTM_EVENT_CHAT_HISTORY_CLICK,
  GTM_EVENT_CHAT_TEXT_CLICK,
  GTM_EVENT_CHAT_TEXT_COPY_CLICK,
  GTM_EVENT_CHAT_UPLOAD_FILE,
  GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
  GTM_EVENT_USER_INTERACTIONS,
} from '@shared/utils/constants/gtm';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { useMounted } from '@shared/utils/hooks/use-mounted';

import { Loader } from '../../components/loader/loader';
import { type BettyBotBlock } from '../../utils/data/graphql/_generated/gql-generated';

// eslint-disable-next-line import/no-unassigned-import
import './betty-bot-block-overrides.scss';
import { MembershipLevel } from '../../utils/enums/membership';

import styles from './betty-bot-block.module.scss';

export const BettyBotBlockClient = ({
  blockData,
  membershipLevel,
  userId,
}: {
  blockData: BettyBotBlock;
  membershipLevel: Nullable<MembershipLevel>;
  userId: string;
}) => {
  const isMounted = useMounted();
  const thumbRef = useRef('');

  let apiToken;
  let isMember = false;
  switch (membershipLevel) {
    case MembershipLevel.COT:
      apiToken = blockData.BettyCOTToken;
      isMember = true;
      break;
    case MembershipLevel.MDRT:
      apiToken = blockData.BettyMDRTToken;
      isMember = true;
      break;
    case MembershipLevel.TOT:
      apiToken = blockData.BettyTOTToken;
      isMember = true;
      break;
    default:
      apiToken = blockData.BettyEveryoneToken;
      break;
  }

  const shouldShowUpload =
    blockData.AllowUploads && (!blockData.HideFileUploadOnPublic || isMember);

  useEffect(() => {
    if (!blockData.BettyApiUrl || !apiToken) return;

    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (target.closest('#btnSend')) {
        const chatInput = document.querySelector<HTMLTextAreaElement>('#input');
        if (chatInput && chatInput.value.trim().length > 0) {
          sendGtmEvent({
            action: GTM_ACTION_CLICK,
            event: GTM_EVENT_USER_INTERACTIONS,
            eventName: GTM_EVENT_CHAT_TEXT_CLICK,
            label: chatInput.value,
          });
        }
        return;
      }

      if (target.closest('#downloadChat')) {
        if (document.querySelectorAll('.questionRow').length > 0) {
          sendGtmEvent({
            action: GTM_ACTION_CLICK,
            event: GTM_EVENT_USER_INTERACTIONS,
            eventName: GTM_EVENT_CHAT_DOWNLOAD_CLICK,
          });
        }
        return;
      }

      const feedbackIcon = target.closest('.feedbackIcon');
      if (feedbackIcon) {
        const icons = document.querySelectorAll('.feedbackIcon');
        const index = Array.prototype.indexOf.call(icons, feedbackIcon);
        const isThumbsDown = index === 0;
        thumbRef.current = isThumbsDown ? 'thumbsDown' : 'thumbsUp';
        sendGtmEvent({
          action: GTM_ACTION_CLICK,
          event: GTM_EVENT_USER_INTERACTIONS,
          eventName: GTM_EVENT_CHAT_FEEDBACK_CLICK,
          label: isThumbsDown ? 'Negative' : 'Positive',
        });
        return;
      }

      const modalButtons = document.querySelectorAll('.modalButton');
      if (modalButtons[1] && (modalButtons[1] === target || modalButtons[1].contains(target))) {
        const inputValue = document.querySelector<HTMLTextAreaElement>('.feedback-input');
        sendGtmEvent({
          action: GTM_ACTION_CLICK,
          event: GTM_EVENT_USER_INTERACTIONS,
          eventName: GTM_EVENT_CHAT_FEEDBACK_INPUT,
          label: thumbRef.current === 'thumbsDown' ? 'Negative' : 'Positive',
          link_label: inputValue?.value || '',
        });
        return;
      }

    const isLearnMoreClick = event
      .composedPath()
      .some((element) => element instanceof Element && element.matches('.referenceTitle'));

    if (isLearnMoreClick) {
      sendGtmEvent({
        action: GTM_ACTION_CLICK,
        event: GTM_EVENT_USER_INTERACTIONS,
        eventName: GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
        label: 'Chat_LearnMore_Topics',
      });
      return;
    }

      const referenceLink = target.closest('.referenceLink') as HTMLAnchorElement | null;
      if (referenceLink) {
        sendGtmEvent({
          action: GTM_ACTION_CLICK,
          event: GTM_EVENT_USER_INTERACTIONS,
          eventName: GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
          label: 'Chat_LearnMore_Topics_click',
          link_label: referenceLink.innerText || '',
          link_url: referenceLink.href || '',
        });
        return;
      }

      const listItem = target.closest('#conversationListWrapper li.list-item');
      if (listItem) {
        const title = listItem.querySelector('span')?.textContent?.trim();
        sendGtmEvent({
          action: GTM_ACTION_CLICK,
          event: GTM_EVENT_USER_INTERACTIONS,
          eventName: GTM_EVENT_CHAT_HISTORY_CLICK,
          label: title,
        });

        const labelSpan = listItem.querySelector('span');
        if (target === labelSpan) {
          const toggleButton = document.querySelector<SVGElement>(
            '.betty-side-menu-icon .menuButtonClosed'
          );
          toggleButton?.dispatchEvent(new MouseEvent('click', { bubbles: true }));
        }
        return;
      }

      const copyButton = target.closest('.copyIcon');
      if (copyButton) {
        const answerRow = copyButton.closest('.answerRow');
        const questionRow = answerRow?.previousElementSibling as HTMLElement | null;
        const questionText = questionRow?.querySelector('.chatBubbleText p')?.textContent?.trim();

        sendGtmEvent({
          action: GTM_ACTION_CLICK,
          event: GTM_EVENT_USER_INTERACTIONS,
          eventName: GTM_EVENT_CHAT_TEXT_COPY_CLICK,
          label: questionText,
        });
      }
    };

    const handleDocumentKeydown = (event: KeyboardEvent) => {
      const target = event.target as HTMLTextAreaElement;
      if (target?.id !== 'input') return;
      if (event.key === 'Enter' && !event.shiftKey && target.value.trim().length > 0) {
        sendGtmEvent({
          action: GTM_ACTION_CLICK,
          event: GTM_EVENT_USER_INTERACTIONS,
          eventName: GTM_EVENT_CHAT_TEXT_CLICK,
          label: target.value,
        });
      }
    };

    const handleFileChange = (event: Event) => {
      const input = event.target as HTMLInputElement;
      if (input.type !== 'file') return;
      const file = input.files?.[0];
      if (!file) return;

      const chatInput = document.querySelector<HTMLTextAreaElement>('#input');
      sendGtmEvent({
        action: GTM_ACTION_CLICK,
        event: GTM_EVENT_USER_INTERACTIONS,
        eventName: GTM_EVENT_CHAT_UPLOAD_FILE,
        link_label: chatInput?.value,
        label: file.name,
      });
    };

    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keydown', handleDocumentKeydown);
    document.addEventListener('change', handleFileChange, true);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('keydown', handleDocumentKeydown);
      document.removeEventListener('change', handleFileChange, true);
    };
  }, [blockData.BettyApiUrl, apiToken]);

  if (!blockData.BettyApiUrl || !apiToken) return null;

  return (
    <>
      <Script async src="https://files.bettybot.ai/ui-widget/betty-bot-ui.js" />
      <link href="https://files.bettybot.ai/ui-widget/betty-bot-ui.css" rel="stylesheet" />
      {(isMounted && (
        <betty-bot
          api-endpoint-token={apiToken}
          api-url={blockData.BettyApiUrl}
          assistant-image={blockData.AssistantImage?.Url}
          className={`betty-chat ${blockData.ChatIconAlignment}`}
          cursor-class="thinking-text"
          disable-download={blockData.DisableDownload ? '1' : '0'}
          display-conversation-list={blockData.ShowConversationList ? '1' : '0'}
          display-file-upload={shouldShowUpload ? '1' : '0'}
          expand-conversation-history-by-default="1"
          info-html={isMember ? blockData.InfoHtmlMembers : blockData.InfoHtml}
          input-placeholder={blockData.InputPlaceholder}
          lower-html={blockData.LowerHtml}
          references-section-collapsed={blockData.ReferencesSectionCollapsed?.toString()}
          references-section-title={blockData.ReferencesSectionTitle}
          sidebar-position-start="closed"
          stream={blockData.ShouldStream ? '1' : '0'}
          terms-url={blockData.TermsUrl}
          user-id={userId}
          user-image={blockData.UserImage?.Url}
        />
      )) || <Loader className={styles['betty-bot-block__loader']} isInner />}
    </>
  );
};
